"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { createClient } from "@/utils/supabase/client";
import { toast } from "sonner";
import { useTranslations } from "next-intl";
import { OnboardingModal, type OnboardingModalVariant } from "@/components/onboarding/onboarding-modal";

type Ctx = {
  openPreferencesEditor: () => Promise<void>;
};

const OnboardingUiContext = createContext<Ctx | null>(null);

export function useOnboardingUi() {
  const v = useContext(OnboardingUiContext);
  if (!v) throw new Error("useOnboardingUi requires OnboardingUiProvider");
  return v;
}

export function OnboardingUiProvider({
  children,
  userCreatedAt,
}: {
  children: React.ReactNode;
  userCreatedAt: string | null;
}) {
  const pathname = usePathname();
  const tToast = useTranslations("settings.preferences");
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [blockingIncomplete, setBlockingIncomplete] = useState(false);
  const [settingsEditor, setSettingsEditor] = useState(false);
  const [hydrate, setHydrate] = useState<{ categories: string[]; role: string | null } | null>(null);
  const [modalKey, setModalKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const supabase = createClient();
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (cancelled) return;
      setSessionUserId(session?.user?.id ?? null);
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname]);

  useEffect(() => {
    if (!sessionUserId) {
      setBlockingIncomplete(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/user/preferences");
        if (!res.ok) throw new Error("prefs");
        const data = (await res.json()) as { onboarding_completed_at: string | null };
        if (cancelled) return;
        setBlockingIncomplete(!data.onboarding_completed_at);
      } catch {
        if (!cancelled) setBlockingIncomplete(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pathname, sessionUserId]);

  const openPreferencesEditor = useCallback(async () => {
    const res = await fetch("/api/user/preferences");
    if (!res.ok) {
      toast.error(tToast("loadError"));
      return;
    }
    const data = (await res.json()) as { categories: string[] | null; role: string | null };
    setHydrate({
      categories: Array.isArray(data.categories) ? data.categories : [],
      role: data.role ?? null,
    });
    setModalKey((k) => k + 1);
    setSettingsEditor(true);
  }, [tToast]);

  const handleDone = useCallback(
    (payload: { kind: "blocking-complete" | "preferences-saved" }) => {
      if (payload.kind === "blocking-complete") {
        setBlockingIncomplete(false);
        try {
          window.dispatchEvent(new Event("kraahl-user-preferences-changed"));
        } catch {
          /* ignore */
        }
      }
      if (payload.kind === "preferences-saved") {
        setSettingsEditor(false);
        setHydrate(null);
        toast.success(tToast("saved"));
        try {
          window.dispatchEvent(new Event("kraahl-user-preferences-changed"));
        } catch {
          /* ignore */
        }
      }
    },
    [tToast]
  );

  const variant: OnboardingModalVariant | null = blockingIncomplete
    ? "blocking"
    : settingsEditor
      ? "preferencesEdit"
      : null;

  const value = useMemo(() => ({ openPreferencesEditor }), [openPreferencesEditor]);

  return (
    <OnboardingUiContext.Provider value={value}>
      {children}
      {variant ? (
        <OnboardingModal
          key={`${variant}-${modalKey}`}
          variant={variant}
          userCreatedAt={userCreatedAt}
          hydrate={variant === "preferencesEdit" ? hydrate : null}
          onDoneAction={handleDone}
        />
      ) : null}
    </OnboardingUiContext.Provider>
  );
}
